import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Copy, Check, ArrowRight, MessageSquare, Phone, Hash,
  Apple, Bot, Shield, Globe, Terminal, CheckCircle, AlertTriangle, Settings
} from 'lucide-react';
import { toast } from 'sonner';

export default function DocsChannelSetup() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-[#3EF1C7]/10 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          Channel Setup
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Messaging Channel Setup
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Connect Commerce Index to Telegram, WhatsApp Business, Slack, or iMessage. Once linked,
          you can manage your store, run commands, and receive alerts from any messaging platform.
        </p>
      </div>

      {/* Telegram */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          <span className="flex items-center gap-3">
            <Bot className="w-6 h-6 text-sky-400" />
            Telegram
          </span>
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Telegram is the fastest way to get started with CI messaging. Create a bot, connect it
          to your CI account, and start issuing commands in under 5 minutes.
        </p>

        <div className="space-y-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-sky-400 font-bold text-sm">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-2">Create a Bot via @BotFather</h3>
                  <p className="text-[#94A3B8] text-sm mb-3">
                    Open Telegram, search for <code className="text-[#3EF1C7]">@BotFather</code>, and send
                    the <code className="text-[#3EF1C7]">/newbot</code> command. Choose a name and username for your bot.
                  </p>
                  <div className="relative">
                    <button
                      onClick={() => copyToClipboard(`# Conversation with @BotFather
You: /newbot
BotFather: Alright, a new bot. How are we going to call it?
You: My Store CI Bot
BotFather: Good. Now let's choose a username.
You: mystoreci_bot
BotFather: Done! Congratulations on your new bot.
         Use this token to access the HTTP API:
         7123456789:AAHk8mN2xR_vB3cDeFgHiJkLmNoPqRsTuVw
         Keep your token secure and store it safely.`, 'telegram-botfather')}
                      className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors z-10"
                    >
                      {copiedCode === 'telegram-botfather' ? (
                        <Check className="w-4 h-4 text-[#3EF1C7]" />
                      ) : (
                        <Copy className="w-4 h-4 text-[#64748B]" />
                      )}
                    </button>
                    <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]"><code>{`# Conversation with @BotFather
You: /newbot
BotFather: Alright, a new bot. How are we going to call it?
You: My Store CI Bot
BotFather: Good. Now let's choose a username.
You: mystoreci_bot
BotFather: Done! Congratulations on your new bot.
         Use this token to access the HTTP API:
         7123456789:AAHk8mN2xR_vB3cDeFgHiJkLmNoPqRsTuVw
         Keep your token secure and store it safely.`}</code></pre>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-sky-400 font-bold text-sm">2</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-2">Configure Webhook URL</h3>
                  <p className="text-[#94A3B8] text-sm mb-3">
                    Register the CI webhook endpoint with Telegram so incoming messages are routed to your CI instance.
                  </p>
                  <div className="relative">
                    <button
                      onClick={() => copyToClipboard(`# Set the webhook URL for your bot
curl -X POST "https://api.telegram.org/bot<YOUR_BOT_TOKEN>/setWebhook" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://api.commerceindex.com/v1/webhooks/telegram",
    "secret_token": "your_ci_webhook_secret",
    "allowed_updates": ["message", "callback_query"]
  }'

# Expected response
{
  "ok": true,
  "result": true,
  "description": "Webhook was set"
}`, 'telegram-webhook')}
                      className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors z-10"
                    >
                      {copiedCode === 'telegram-webhook' ? (
                        <Check className="w-4 h-4 text-[#3EF1C7]" />
                      ) : (
                        <Copy className="w-4 h-4 text-[#64748B]" />
                      )}
                    </button>
                    <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]"><code>{`# Set the webhook URL for your bot
curl -X POST "https://api.telegram.org/bot<YOUR_BOT_TOKEN>/setWebhook" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://api.commerceindex.com/v1/webhooks/telegram",
    "secret_token": "your_ci_webhook_secret",
    "allowed_updates": ["message", "callback_query"]
  }'

# Expected response
{
  "ok": true,
  "result": true,
  "description": "Webhook was set"
}`}</code></pre>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-sky-400 font-bold text-sm">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-2">Set Bot Commands</h3>
                  <p className="text-[#94A3B8] text-sm mb-3">
                    Register slash commands with Telegram so users get autocomplete suggestions.
                  </p>
                  <div className="relative">
                    <button
                      onClick={() => copyToClipboard(`curl -X POST "https://api.telegram.org/bot<YOUR_TOKEN>/setMyCommands" \\
  -H "Content-Type: application/json" \\
  -d '{"commands": [
    {"command": "pulse", "description": "Quick health check"},
    {"command": "brief", "description": "Daily business summary"},
    {"command": "deals", "description": "List and manage deals"},
    {"command": "inventory", "description": "Check stock levels"},
    {"command": "agents", "description": "View active agents"},
    {"command": "inbox", "description": "Pending approvals"},
    {"command": "help", "description": "Show all commands"}
  ]}'`, 'telegram-commands')}
                      className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors z-10"
                    >
                      {copiedCode === 'telegram-commands' ? (
                        <Check className="w-4 h-4 text-[#3EF1C7]" />
                      ) : (
                        <Copy className="w-4 h-4 text-[#64748B]" />
                      )}
                    </button>
                    <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]"><code>{`curl -X POST "https://api.telegram.org/bot<YOUR_TOKEN>/setMyCommands" \\
  -H "Content-Type: application/json" \\
  -d '{"commands": [
    {"command": "pulse", "description": "Quick health check"},
    {"command": "brief", "description": "Daily business summary"},
    {"command": "deals", "description": "List and manage deals"},
    {"command": "inventory", "description": "Check stock levels"},
    {"command": "agents", "description": "View active agents"},
    {"command": "inbox", "description": "Pending approvals"},
    {"command": "help", "description": "Show all commands"}
  ]}'`}</code></pre>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-sky-400 font-bold text-sm">4</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Add Token in CI Settings</h3>
                  <p className="text-[#94A3B8] text-sm">
                    Go to <strong className="text-white">Settings &gt; Channels &gt; Telegram</strong> in your
                    CI dashboard. Paste your bot token and webhook secret. Click <strong className="text-white">Connect</strong>.
                    Send <code className="text-[#3EF1C7]">/help</code> to your bot to verify the connection.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* WhatsApp */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          <span className="flex items-center gap-3">
            <Phone className="w-6 h-6 text-emerald-400" />
            WhatsApp Business
          </span>
        </h2>
        <p className="text-[#94A3B8] mb-4">
          WhatsApp Business integration uses the Meta Cloud API. You need a Meta Business account and
          a verified phone number dedicated to your CI bot.
        </p>

        <div className="space-y-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-emerald-400 font-bold text-sm">1</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Create a Meta Business Account</h3>
                  <p className="text-[#94A3B8] text-sm">
                    Go to <code className="text-[#3EF1C7]">business.facebook.com</code> and create a
                    business account. Navigate to Meta for Developers, create a new app, and add the WhatsApp product.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-emerald-400 font-bold text-sm">2</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Register a Phone Number</h3>
                  <p className="text-[#94A3B8] text-sm">
                    Add a phone number to your WhatsApp Business Account. This number must not be
                    registered with any personal WhatsApp account. Meta verifies it via SMS or voice call.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-emerald-400 font-bold text-sm">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-2">Configure Webhook Verification</h3>
                  <p className="text-[#94A3B8] text-sm mb-3">
                    Meta requires webhook verification via a GET challenge. CI handles this automatically.
                  </p>
                  <div className="relative">
                    <button
                      onClick={() => copyToClipboard(`# Webhook configuration in Meta Developer Portal:
Callback URL:    https://api.commerceindex.com/v1/webhooks/whatsapp
Verify Token:    your_ci_whatsapp_verify_token

# Subscribed webhook fields:
- messages              # Incoming messages from users
- message_deliveries    # Delivery status updates
- message_reads         # Read receipt notifications

# Meta sends a GET request to verify:
# GET /webhooks/whatsapp?hub.mode=subscribe
#     &hub.verify_token=YOUR_VERIFY_TOKEN
#     &hub.challenge=CHALLENGE_STRING
# CI responds with the challenge string if token matches`, 'whatsapp-webhook')}
                      className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors z-10"
                    >
                      {copiedCode === 'whatsapp-webhook' ? (
                        <Check className="w-4 h-4 text-[#3EF1C7]" />
                      ) : (
                        <Copy className="w-4 h-4 text-[#64748B]" />
                      )}
                    </button>
                    <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]"><code>{`# Webhook configuration in Meta Developer Portal:
Callback URL:    https://api.commerceindex.com/v1/webhooks/whatsapp
Verify Token:    your_ci_whatsapp_verify_token

# Subscribed webhook fields:
- messages              # Incoming messages from users
- message_deliveries    # Delivery status updates
- message_reads         # Read receipt notifications

# Meta sends a GET request to verify:
# GET /webhooks/whatsapp?hub.mode=subscribe
#     &hub.verify_token=YOUR_VERIFY_TOKEN
#     &hub.challenge=CHALLENGE_STRING
# CI responds with the challenge string if token matches`}</code></pre>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-emerald-400 font-bold text-sm">4</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-2">Set Up Message Templates</h3>
                  <p className="text-[#94A3B8] text-sm mb-3">
                    WhatsApp requires pre-approved templates for business-initiated messages (daily briefs, alerts).
                  </p>
                  <div className="relative">
                    <button
                      onClick={() => copyToClipboard(`# Required message templates for CI

Template: ci_daily_brief
Category: UTILITY
Body: "{{1}} Daily Brief for {{2}}
Revenue: {{3}} | Orders: {{4}} | Health Score: {{5}}
View full report: {{6}}"

Template: ci_alert
Category: UTILITY
Body: "Alert: {{1}}
{{2}}
Action required: {{3}}"

Template: ci_approval_request
Category: UTILITY
Body: "Approval needed: {{1}}
Agent: {{2}} | Confidence: {{3}}
Details: {{4}}
Reply APPROVE or REJECT"`, 'whatsapp-templates')}
                      className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors z-10"
                    >
                      {copiedCode === 'whatsapp-templates' ? (
                        <Check className="w-4 h-4 text-[#3EF1C7]" />
                      ) : (
                        <Copy className="w-4 h-4 text-[#64748B]" />
                      )}
                    </button>
                    <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]"><code>{`# Required message templates for CI

Template: ci_daily_brief
Category: UTILITY
Body: "{{1}} Daily Brief for {{2}}
Revenue: {{3}} | Orders: {{4}} | Health Score: {{5}}
View full report: {{6}}"

Template: ci_alert
Category: UTILITY
Body: "Alert: {{1}}
{{2}}
Action required: {{3}}"

Template: ci_approval_request
Category: UTILITY
Body: "Approval needed: {{1}}
Agent: {{2}} | Confidence: {{3}}
Details: {{4}}
Reply APPROVE or REJECT"`}</code></pre>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-emerald-400 font-bold text-sm">5</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Add Credentials in CI Settings</h3>
                  <p className="text-[#94A3B8] text-sm">
                    Go to <strong className="text-white">Settings &gt; Channels &gt; WhatsApp</strong>. Enter your
                    Phone Number ID, WhatsApp Business Account ID, permanent access token, and verify token.
                    Click <strong className="text-white">Connect</strong>.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Slack */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          <span className="flex items-center gap-3">
            <Hash className="w-6 h-6 text-purple-400" />
            Slack
          </span>
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Slack integration enables CI commands via slash commands, approval workflows in channels,
          and automated notifications. Ideal for teams that already live in Slack.
        </p>

        <div className="space-y-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-400 font-bold text-sm">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-2">Create a Slack App</h3>
                  <p className="text-[#94A3B8] text-sm mb-3">
                    Go to <code className="text-[#3EF1C7]">api.slack.com/apps</code>, click "Create New App",
                    choose "From scratch", and add these OAuth scopes:
                  </p>
                  <div className="relative">
                    <button
                      onClick={() => copyToClipboard(`# Required OAuth Scopes (Bot Token Scopes)
chat:write        # Send messages to channels
commands          # Register and respond to slash commands
users:read        # Read user profile info for account linking
files:write       # Upload reports and charts
reactions:read    # Track approval reactions
im:history        # Read DM history for command context
im:write          # Send DMs for private notifications`, 'slack-scopes')}
                      className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors z-10"
                    >
                      {copiedCode === 'slack-scopes' ? (
                        <Check className="w-4 h-4 text-[#3EF1C7]" />
                      ) : (
                        <Copy className="w-4 h-4 text-[#64748B]" />
                      )}
                    </button>
                    <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]"><code>{`# Required OAuth Scopes (Bot Token Scopes)
chat:write        # Send messages to channels
commands          # Register and respond to slash commands
users:read        # Read user profile info for account linking
files:write       # Upload reports and charts
reactions:read    # Track approval reactions
im:history        # Read DM history for command context
im:write          # Send DMs for private notifications`}</code></pre>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-400 font-bold text-sm">2</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Install to Your Workspace</h3>
                  <p className="text-[#94A3B8] text-sm">
                    Go to <strong className="text-white">OAuth & Permissions</strong>, add the scopes above,
                    then click <strong className="text-white">Install to Workspace</strong>. Copy the Bot User OAuth Token.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-400 font-bold text-sm">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-2">Configure Slash Commands</h3>
                  <p className="text-[#94A3B8] text-sm mb-3">
                    In <strong className="text-white">Slash Commands</strong>, create a single command that routes
                    all CI commands through one endpoint.
                  </p>
                  <div className="relative">
                    <button
                      onClick={() => copyToClipboard(`# Slash Command Configuration
Command:           /ci
Request URL:       https://api.commerceindex.com/v1/webhooks/slack/command
Short Description: Commerce Index commands
Usage Hint:        [pulse | brief | deals | inventory | agents | help]

# Example usage in Slack:
/ci pulse          # Quick health check
/ci brief          # Daily summary
/ci deals          # List active deals
/ci inventory      # Stock level overview`, 'slack-commands')}
                      className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors z-10"
                    >
                      {copiedCode === 'slack-commands' ? (
                        <Check className="w-4 h-4 text-[#3EF1C7]" />
                      ) : (
                        <Copy className="w-4 h-4 text-[#64748B]" />
                      )}
                    </button>
                    <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]"><code>{`# Slash Command Configuration
Command:           /ci
Request URL:       https://api.commerceindex.com/v1/webhooks/slack/command
Short Description: Commerce Index commands
Usage Hint:        [pulse | brief | deals | inventory | agents | help]

# Example usage in Slack:
/ci pulse          # Quick health check
/ci brief          # Daily summary
/ci deals          # List active deals
/ci inventory      # Stock level overview`}</code></pre>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-400 font-bold text-sm">4</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Connect in CI Settings</h3>
                  <p className="text-[#94A3B8] text-sm">
                    Go to <strong className="text-white">Settings &gt; Channels &gt; Slack</strong>. Enter your
                    Bot Token, Signing Secret, and default channel. Click <strong className="text-white">Connect</strong>.
                    Test with <code className="text-[#3EF1C7]">/ci help</code> in any channel.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* iMessage */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          <span className="flex items-center gap-3">
            <Apple className="w-6 h-6 text-[#94A3B8]" />
            iMessage
          </span>
        </h2>
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-[#94A3B8]">
            <strong className="text-white">Beta.</strong> Apple provides no public iMessage API,
            so iMessage connects through a <strong className="text-white">relay</strong> — a Mac
            signed into Messages.app, or a hosted iMessage vendor. iMessage runs over data:
            every message is free; the relay itself is the only cost. Until a relay is deployed,
            iPhone users can simply <strong className="text-white">text our number</strong> —
            iMessage falls back to SMS automatically and a single text signs you up.
          </p>
        </div>

        <div className="space-y-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-[#94A3B8]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#94A3B8] font-bold text-sm">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-2">How the relay contract works</h3>
                  <p className="text-[#94A3B8] text-sm mb-3">
                    Commerce Index is relay-agnostic. Any relay that speaks this contract works —
                    inbound messages POST to our webhook; outbound replies POST to the relay:
                  </p>
                  <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]"><code>{`# Inbound (relay → Commerce Index)
POST https://<api-host>/api/v2/messaging/webhooks/imessage
X-Relay-Secret: <IMESSAGE_RELAY_SECRET>
{"from": "+15551234567", "text": "START"}

# Outbound (Commerce Index → relay)
POST <IMESSAGE_RELAY_URL>/send
Authorization: Bearer <IMESSAGE_RELAY_SECRET>
{"to": "+15551234567", "text": "Welcome to Commerce Index..."}

# Backend env vars
IMESSAGE_RELAY_URL=https://your-relay.example.com
IMESSAGE_RELAY_SECRET=<shared secret>`}</code></pre>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-[#94A3B8]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#94A3B8] font-bold text-sm">2</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Option A — Mac relay (reference implementation)</h3>
                  <p className="text-[#94A3B8] text-sm">
                    A Mac (mini recommended, M1+) signed into Messages.app with a dedicated Apple ID.
                    The reference bridge in <code className="text-[#3EF1C7]">openclaw/skills/ci-platforms/imessage.ts</code>{' '}
                    sends via AppleScript and polls <code className="text-[#3EF1C7]">~/Library/Messages/chat.db</code>{' '}
                    (requires Full Disk Access in System Settings &gt; Privacy &amp; Security).
                    Wrap it with the relay contract above and expose it to the backend
                    (a Cloudflare Tunnel works well behind NAT). Cost: the Mac itself.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-[#94A3B8]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#94A3B8] font-bold text-sm">3</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Option B — Hosted iMessage vendor</h3>
                  <p className="text-[#94A3B8] text-sm">
                    Hosted iMessage API vendors run the Macs for you and can adapt to the same
                    relay contract. Be aware these services operate in a gray area of Apple's
                    terms and can experience disruptions — treat iMessage as an enhancement,
                    with WhatsApp/SMS as the dependable primary channels.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-[#94A3B8]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#94A3B8] font-bold text-sm">4</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">What works once connected</h3>
                  <p className="text-[#94A3B8] text-sm">
                    Inbound-first signup (a first iMessage from an unknown number provisions the
                    account, exactly like WhatsApp), the full conversational assistant, all
                    commands, and Apple-ID email handles linked to existing accounts by email
                    match. Every message is free — iMessage is data-based.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Platform Comparison */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Platform Comparison
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#22304A]">
                  <th className="text-left text-[#94A3B8] p-4 font-medium">Feature</th>
                  <th className="text-left text-[#94A3B8] p-4 font-medium">Telegram</th>
                  <th className="text-left text-[#94A3B8] p-4 font-medium">WhatsApp</th>
                  <th className="text-left text-[#94A3B8] p-4 font-medium">Slack</th>
                  <th className="text-left text-[#94A3B8] p-4 font-medium">iMessage</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#22304A]/50">
                  <td className="text-white p-4">Setup</td>
                  <td className="text-[#3EF1C7] p-4">Easy</td>
                  <td className="text-amber-400 p-4">Medium</td>
                  <td className="text-amber-400 p-4">Medium</td>
                  <td className="text-rose-400 p-4">Advanced</td>
                </tr>
                <tr className="border-b border-[#22304A]/50">
                  <td className="text-white p-4">All Commands</td>
                  <td className="text-[#3EF1C7] p-4">Yes</td>
                  <td className="text-[#3EF1C7] p-4">Yes</td>
                  <td className="text-[#3EF1C7] p-4">Yes</td>
                  <td className="text-[#3EF1C7] p-4">Yes</td>
                </tr>
                <tr className="border-b border-[#22304A]/50">
                  <td className="text-white p-4">Inline Buttons</td>
                  <td className="text-[#3EF1C7] p-4">Yes</td>
                  <td className="text-[#94A3B8] p-4">No</td>
                  <td className="text-[#3EF1C7] p-4">Yes</td>
                  <td className="text-[#94A3B8] p-4">No</td>
                </tr>
                <tr className="border-b border-[#22304A]/50">
                  <td className="text-white p-4">Hardware</td>
                  <td className="text-[#3EF1C7] p-4">None</td>
                  <td className="text-[#3EF1C7] p-4">None</td>
                  <td className="text-[#3EF1C7] p-4">None</td>
                  <td className="text-amber-400 p-4">Mac Mini</td>
                </tr>
                <tr>
                  <td className="text-white p-4">Cost</td>
                  <td className="text-[#3EF1C7] p-4">Free</td>
                  <td className="text-amber-400 p-4">Meta fees</td>
                  <td className="text-[#3EF1C7] p-4">Free</td>
                  <td className="text-amber-400 p-4">Mac cost</td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
      </section>

      {/* What's Next? */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6">
          What's Next?
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/docs/commands">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/30 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Messaging Commands <ArrowRight className="inline w-4 h-4" /></h3>
                <p className="text-sm text-[#94A3B8]">Full command reference for all channels</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/docs/account-linking">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/30 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Account Linking <ArrowRight className="inline w-4 h-4" /></h3>
                <p className="text-sm text-[#94A3B8]">Link your messaging identity to your CI account</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
}
